import type { ExternalAlert } from '@/lib/content-types';
import { HomeHScrollTrack } from '@/components/site/home-h-scroll';
import { getProviderIcon } from '@/lib/provider-icons';
import { getTimeAgo } from '@/lib/timeUtils';
import { cn } from '@/lib/utils';

/**
 * Homepage main-column מבזקים rail: national top headlines from major
 * Israeli news sites (no local competitor outlets).
 */
export function HomeAlertsStrip({
    alerts = [],
    className,
}: {
    alerts?: ExternalAlert[];
    className?: string;
}) {
    if (alerts.length === 0) return null;

    return (
        <section
            className={cn('border-white/10', className)}
            aria-label="מבזקים"
        >
            <div className="flex items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
                <p className="shrink-0 text-sm text-white/55">מבזקים</p>
                <HomeHScrollTrack
                    className="min-w-0 flex-1"
                    prevLabel="מבזקים קודמים"
                    nextLabel="מבזקים הבאים"
                    resetKey={alerts.map((alert) => alert.$id).join(',')}
                >
                    <ul className="flex w-max items-stretch gap-0">
                        {alerts.map((alert, index) => {
                            const when = alert.publishedAt || alert.firstSeenAt;
                            const Icon = getProviderIcon(alert.provider);
                            return (
                                <li
                                    key={alert.$id}
                                    className={cn(
                                        'flex shrink-0 items-center',
                                        index < alerts.length - 1 &&
                                            'border-e border-white/10',
                                    )}
                                >
                                    <a
                                        href={alert.url}
                                        target="_blank"
                                        rel="nofollow noopener noreferrer"
                                        className="flex max-w-[min(100vw-6rem,32rem)] min-w-[12rem] items-center gap-3 px-4 py-1 text-white transition-colors hover:text-white/85 sm:min-w-[16rem]"
                                    >
                                        {Icon ? (
                                            <Icon className="shrink-0" />
                                        ) : null}
                                        <span className="min-w-0">
                                            <span className="title-single-line block text-sm font-normal leading-snug tracking-tight">
                                                {alert.title}
                                            </span>
                                            <span className="mt-0.5 flex items-center gap-2 text-xs text-white/55">
                                                <span className="truncate">
                                                    {alert.providerLabel}
                                                </span>
                                                {when ? (
                                                    <>
                                                        <span
                                                            aria-hidden
                                                            className="text-white/20"
                                                        >
                                                            ·
                                                        </span>
                                                        <time
                                                            dateTime={when}
                                                            className="shrink-0"
                                                        >
                                                            {getTimeAgo(when)}
                                                        </time>
                                                    </>
                                                ) : null}
                                            </span>
                                        </span>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </HomeHScrollTrack>
            </div>
        </section>
    );
}
